import React, { Component } from "react";
import { StyledIconBase } from "@styled-icons/styled-icon";
import { CheckCircle, DirectionsRun } from "@styled-icons/material";
import styled from "styled-components/macro";
import List, { ListItem } from "../../components/List";
import {
  BUYER_REWARD_CONDITION_SHORT,
  REWARD_CONDITION_SHORT,
  amountForCopy,
  rewardForCopy,
  voucherForCopy,
} from "../../utils/referralUtils";
import { mediaQueries } from "../../styles/media";

const StyledTodoSummary = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;

  color: ${(props) => (props.done ? "var(--juno-color-header)" : "var(--juno-color-text)")};
  background-color: ${(props) => (props.done ? "var(--juno-color-pale-green)" : "var(--juno-color-pink)")};

  padding-top: 1em;
  padding-bottom: 1em;
  ${mediaQueries.md} {
    padding-top: 1.5em;
    padding-bottom: 1.5em;
  }

  margin-top: -1em;
  margin-bottom: 1em;
  ${mediaQueries.md} {
    margin-top: -2em;
  }
`;

const TodoSummaryContent = styled.div`
  margin-left: 20px;
  margin-right: 20px;

  ${mediaQueries.md} {
    margin-left: 45px;
    margin-right: 45px;
  }

  ${mediaQueries.lg} {
    width: 930px;
    margin-left: auto;
    margin-right: auto;
  }

  p,
  ul,
  ul li {
    font-size: var(--juno-font-size-body-sm);
    line-height: 1.5;
  }

  ul:last-child {
    margin-bottom: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  h3 {
    font-size: 18px;
    ${mediaQueries.md} {
      font-size: 20px;
    }
    line-height: 1.3157894737;
    font-weight: var(--juno-font-weight-bold);
    margin: 0 0 1em 0;
  }

  a,
  a:link,
  a:visited {
    font-size: var(--juno-font-size-body-sm);
    text-decoration: none;
    color: var(--juno-color-text);
    border-bottom: 1px solid transparent;
    &:hover {
      border-bottom-color: inherit;
    }
  }
`;

export const TodoSummaryHeading = styled.h3`
  display: flex;
  align-items: center;

  font-size: var(--juno-font-size-h4-sm);
  ${mediaQueries.md} {
    font-size: 19px;
  }
`;

export const TodoSummaryIcon = styled.div`
  ${StyledIconBase} {
    width: var(--juno-icon-size-xl);
    margin-right: 10px;
    ${mediaQueries.md} {
      margin-right: 16px;
    }
  }
`;

const triggerScroll = (id) => (e) => {
  e.preventDefault();
  const target = document.getElementById(id);
  if (!!target) {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  } else {
    console.warn("attempt to scroll to missing element", id);
  }
};

const ScrollLink = (props) => {
  return (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid -- Scroll navigation control; button would break existing styles
    <a href="#" onClick={triggerScroll(props.toId)}>
      {props.children}
    </a>
  );
};

// The one-line ask at the top of the dashboard, pointing at the referral card
// at the bottom. Amounts come from the referral code, never from here.
const referralLineFor = (prompt, referral) => {
  const discount = amountForCopy(referral.discountAmount);
  const hasReward = !!rewardForCopy(referral.rewardAmount);
  const voucher = voucherForCopy(referral.rewardAmount);
  if (prompt === "refer_buyer") {
    return hasReward
      ? `Selling? Get your buyer to use Juno and earn ${voucher} ${BUYER_REWARD_CONDITION_SHORT} →`
      : "Selling? Get your buyer to use Juno →";
  }
  return hasReward
    ? `Refer a friend: they get ${discount} off, you get ${voucher} ${REWARD_CONDITION_SHORT} →`
    : `Refer a friend: they get ${discount} off →`;
};

const ReferralLine = ({ prompt, referral }) => {
  if (!referral) return null;
  return (
    <p>
      <ScrollLink toId="refer">{referralLineFor(prompt, referral)}</ScrollLink>
    </p>
  );
};

class TodoSummary extends Component {
  render() {
    const selectedCase = this.props.selectedCase;
    const referralLine = <ReferralLine prompt={selectedCase.client_referral_prompt} referral={this.props.referral} />;

    let unreadNotifications = [];
    if (selectedCase.notifications) {
      unreadNotifications = selectedCase.notifications.filter((n) => n.status === "unread");
    }

    const enquiryDoables = selectedCase.doables.filter(
      (d) => d.doable_type === "enquiry" && (d.status === "available" || d.status === "available_to_read"),
    );

    const availableDoables = selectedCase.doables.filter((d) => d.status === "available");
    const numberOfFormsToFillIn = availableDoables.filter(
      (d) => d.doable_type !== "emailed_request" && d.doable_type !== "document_request" && d.doable_type !== "enquiry",
    ).length;
    const numberOfDocumentRequests = availableDoables.filter((d) => d.doable_type === "document_request").length;
    const numberOfEmailedRequests = availableDoables.filter((d) => d.doable_type === "emailed_request").length;

    const hasSummaryItems =
      availableDoables.length > 0 ||
      unreadNotifications.length > 0 ||
      selectedCase.enquiries_waiting_for_client > 0 ||
      selectedCase.enquiries_waiting_for_third_party > 0;

    if (!hasSummaryItems) {
      return (
        <StyledTodoSummary done>
          <TodoSummaryContent>
            <TodoSummaryHeading>
              <TodoSummaryIcon>
                <CheckCircle />
              </TodoSummaryIcon>
              <span>We’ve got everything we need right now</span>
            </TodoSummaryHeading>
            <p>We’ll send you an email whenever there’s something for you to do.</p>
            {referralLine}
          </TodoSummaryContent>
        </StyledTodoSummary>
      );
    }

    return (
      <StyledTodoSummary>
        <TodoSummaryContent>
          <TodoSummaryHeading>
            <TodoSummaryIcon>
              <DirectionsRun />
            </TodoSummaryIcon>
            <span>Help keep your case moving</span>
          </TodoSummaryHeading>

          {/* Each link's text is one string, so React can update it after Chrome Translate rewrites the text */}
          <List bullet>
            {numberOfFormsToFillIn > 0 && (
              <ListItem>
                <ScrollLink toId="fill-in-forms">
                  {`${numberOfFormsToFillIn} ${numberOfFormsToFillIn > 1 ? "forms" : "form"} to fill in`}
                </ScrollLink>
              </ListItem>
            )}

            {numberOfDocumentRequests > 0 && (
              <ListItem>
                <ScrollLink toId="upload-documents">
                  {`${numberOfDocumentRequests} ${numberOfDocumentRequests > 1 ? "documents" : "document"} to upload`}
                </ScrollLink>
              </ListItem>
            )}

            {numberOfEmailedRequests > 0 && (
              <ListItem>
                <ScrollLink toId="other-things-we-need">
                  {`${numberOfEmailedRequests} other ${numberOfEmailedRequests > 1 ? "things" : "thing"} we need`}
                </ScrollLink>
              </ListItem>
            )}

            {unreadNotifications.length > 0 && (
              <ListItem>
                <ScrollLink toId="messages">
                  {`${unreadNotifications.length} unread ${unreadNotifications.length > 1 ? "messages" : "message"}`}
                </ScrollLink>
              </ListItem>
            )}

            {enquiryDoables.length > 0 && (
              <ListItem>
                <ScrollLink toId="enquiries">
                  {`${enquiryDoables.length} ${enquiryDoables.length > 1 ? "enquiries" : "enquiry"} to read or respond to`}
                </ScrollLink>
              </ListItem>
            )}

            {selectedCase.enquiries_waiting_for_client > 0 && (
              <ListItem>
                <ScrollLink toId="client-enquiries">
                  {`${selectedCase.enquiries_waiting_for_client} ${selectedCase.enquiries_waiting_for_client > 1 ? "questions" : "question"} awaiting your reply`}
                </ScrollLink>
              </ListItem>
            )}

            {selectedCase.enquiries_waiting_for_third_party > 0 && (
              <ListItem>
                <ScrollLink toId="third-party-enquiries">
                  {`${selectedCase.enquiries_waiting_for_third_party} ${selectedCase.enquiries_waiting_for_third_party > 1 ? "questions" : "question"} awaiting a reply from your management company, lender or another third party`}
                </ScrollLink>
              </ListItem>
            )}
          </List>

          {!!selectedCase.status_updates && (
            <ScrollLink toId="current-status">See the current status of your {this.props.transactionType}</ScrollLink>
          )}
          {referralLine}
        </TodoSummaryContent>
      </StyledTodoSummary>
    );
  }
}

export default TodoSummary;
